/* ==========================================================================
   PiHOT Header — mega menu + cart badge
   ========================================================================== */

(() => {
  const OPEN_DELAY = 120;
  const CLOSE_DELAY = 200;
  const TAB_HOVER_DELAY = 80;

  if (!customElements.get('pihot-mega-menu')) {
    class PihotMegaMenu extends HTMLElement {
      static current = null;

      connectedCallback() {
        this.item = this.closest('li');
        this.trigger = this.item?.querySelector(':scope > a');
        this.header = this.closest('.header');
        this.panel = this.querySelector('.p-mega__panel');
        if (!this.item || !this.trigger || !this.panel) return;

        this.openTimer = null;
        this.closeTimer = null;
        this.tabTimer = null;
        this.lastPointerType = 'mouse';

        this.onPointerEnter = this.onPointerEnter.bind(this);
        this.onPointerLeave = this.onPointerLeave.bind(this);
        this.onTriggerClick = this.onTriggerClick.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);

        this.item.addEventListener('pointerenter', this.onPointerEnter);
        this.item.addEventListener('pointerleave', this.onPointerLeave);
        this.item.addEventListener('focusout', this.onFocusOut);
        this.item.addEventListener('keydown', this.onKeyDown);
        this.trigger.addEventListener('pointerdown', (e) => (this.lastPointerType = e.pointerType));
        this.trigger.addEventListener('click', this.onTriggerClick);
      }

      disconnectedCallback() {
        if (PihotMegaMenu.current === this) this.close();
        if (!this.item) return;
        this.item.removeEventListener('pointerenter', this.onPointerEnter);
        this.item.removeEventListener('pointerleave', this.onPointerLeave);
        this.item.removeEventListener('focusout', this.onFocusOut);
        this.item.removeEventListener('keydown', this.onKeyDown);
      }

      get isOpen() {
        return this.item.classList.contains('is-open');
      }

      hydrate() {
        if (this.hydrated) return;
        const template = this.querySelector('template[data-p-mega-template]');
        if (!template) return;
        this.panel.appendChild(template.content.cloneNode(true));
        template.remove();
        this.hydrated = true;
        this.setupTabs();
        this.panel.querySelector('[data-p-mega-close]')?.addEventListener('click', () => {
          this.close();
          this.trigger.focus();
        });
      }

      setupTabs() {
        this.tabs = Array.from(this.panel.querySelectorAll('[role="tab"]'));
        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.selectTab(index));
          tab.addEventListener('pointerenter', (e) => {
            if (e.pointerType !== 'mouse') return;
            clearTimeout(this.tabTimer);
            this.tabTimer = setTimeout(() => this.selectTab(index), TAB_HOVER_DELAY);
          });
          tab.addEventListener('pointerleave', () => clearTimeout(this.tabTimer));
          tab.addEventListener('keydown', (e) => this.onTabKeyDown(e, index));
        });
      }

      selectTab(index, focus = false) {
        if (!this.tabs?.[index]) return;
        if (this.tabs[index].getAttribute('aria-selected') === 'true') return;
        this.panel.scrollTop = 0;
        this.toggleAttribute('data-p-switched', true);
        this.tabs.forEach((tab, i) => {
          const selected = i === index;
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
          const panel = this.tabPanelOf(tab);
          if (panel) panel.hidden = !selected;
        });
        if (focus) this.tabs[index].focus();
      }

      tabPanelOf(tab) {
        return this.panel.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
      }

      /* One height for every series, so switching tabs never resizes the panel: the tallest
         series sets it, and if any series overflows the room below the header the whole
         menu goes full screen (.p-mega--full). Measured on open and resize only. */
      fit() {
        const inner = this.panel.querySelector('.p-mega__inner');
        if (!inner || !this.isOpen) return;
        const room = Math.max(0, window.innerHeight - this.panel.getBoundingClientRect().top);
        this.style.setProperty('--p-mega-room', `${room}px`);
        this.classList.remove('p-mega--full');
        this.style.removeProperty('--p-mega-min');

        const panels = (this.tabs || []).map((tab) => this.tabPanelOf(tab)).filter(Boolean);
        const shown = panels.filter((panel) => !panel.hidden);
        let tallest = inner.offsetHeight;
        panels.forEach((panel) => {
          if (!panel.hidden) return;
          shown.forEach((open) => (open.hidden = true));
          panel.hidden = false;
          tallest = Math.max(tallest, inner.offsetHeight);
          panel.hidden = true;
          shown.forEach((open) => (open.hidden = false));
        });

        const full = tallest > room - 40;
        this.classList.toggle('p-mega--full', full);
        if (!full) this.style.setProperty('--p-mega-min', `${tallest + this.panel.offsetHeight - inner.offsetHeight}px`);
      }

      onTabKeyDown(event, index) {
        const last = this.tabs.length - 1;
        const next = index === last ? 0 : index + 1;
        const prev = index === 0 ? last : index - 1;
        const keyMap = { ArrowDown: next, ArrowRight: next, ArrowUp: prev, ArrowLeft: prev, Home: 0, End: last };
        if (!(event.key in keyMap)) return;
        event.preventDefault();
        this.selectTab(keyMap[event.key], true);
      }

      open() {
        clearTimeout(this.closeTimer);
        this.hydrate();
        if (this.isOpen) return;
        if (PihotMegaMenu.current && PihotMegaMenu.current !== this) PihotMegaMenu.current.close();
        PihotMegaMenu.current = this;
        this.removeAttribute('data-p-switched');
        this.item.classList.add('is-open');
        this.header?.classList.add('p-mega-open');
        this.trigger.setAttribute('aria-expanded', 'true');
        this.fit();
      }

      close() {
        clearTimeout(this.openTimer);
        clearTimeout(this.closeTimer);
        clearTimeout(this.tabTimer);
        if (!this.isOpen) return;
        this.item.classList.remove('is-open');
        this.trigger.setAttribute('aria-expanded', 'false');
        if (PihotMegaMenu.current === this) {
          PihotMegaMenu.current = null;
          this.header?.classList.remove('p-mega-open');
        }
      }

      onPointerEnter(event) {
        if (event.pointerType !== 'mouse') return;
        this.hydrate();
        clearTimeout(this.closeTimer);
        clearTimeout(this.openTimer);
        const delay = PihotMegaMenu.current ? 0 : OPEN_DELAY;
        this.openTimer = setTimeout(() => this.open(), delay);
      }

      onPointerLeave(event) {
        if (event.pointerType !== 'mouse') return;
        clearTimeout(this.openTimer);
        this.closeTimer = setTimeout(() => this.close(), CLOSE_DELAY);
      }

      onTriggerClick(event) {
        const href = this.trigger.getAttribute('href');
        if (!href || href === '#') {
          event.preventDefault();
          if (this.isOpen && this.lastPointerType !== 'mouse') this.close();
          else this.open();
          return;
        }
        if (this.lastPointerType === 'mouse' || this.isOpen) return;
        event.preventDefault();
        this.open();
      }

      onFocusOut(event) {
        if (!this.item.contains(event.relatedTarget)) this.close();
      }

      onKeyDown(event) {
        if (event.key === 'Escape' && this.isOpen) {
          event.stopPropagation();
          this.close();
          this.trigger.focus();
          return;
        }
        if (event.target === this.trigger && event.key === 'ArrowDown') {
          event.preventDefault();
          this.open();
          const selected = this.tabs?.find((tab) => tab.getAttribute('aria-selected') === 'true');
          (selected || this.tabs?.[0])?.focus();
        }
      }
    }

    customElements.define('pihot-mega-menu', PihotMegaMenu);

    window.addEventListener('resize', () => PihotMegaMenu.current?.fit());

    document.addEventListener('pointerdown', (event) => {
      const current = PihotMegaMenu.current;
      if (current && !current.item.contains(event.target)) current.close();
    });

    document.addEventListener('shopify:block:select', (event) => {
      if (event.target instanceof PihotMegaMenu) event.target.open();
    });

    document.addEventListener('shopify:block:deselect', (event) => {
      if (event.target instanceof PihotMegaMenu) event.target.close();
    });
  }

  /* Menu items whose link is "#" only open a panel; following the link would
     jump the page back to the top. */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('.header a[href="#"], #mobile-menu a[href="#"]');
    if (link) event.preventDefault();
  });

  /* ---------- Mobile menu accordions (one open per level) ---------- */

  const setExpanded = (toggle, expanded) => {
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;
    toggle.setAttribute('aria-expanded', String(expanded));
    panel.hidden = !expanded;
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('#mobile-menu [data-p-accordion]');
    if (!toggle) return;

    const expand = toggle.getAttribute('aria-expanded') !== 'true';
    if (expand) {
      const list = toggle.closest('li')?.parentElement;
      list?.querySelectorAll(':scope > li > [data-p-accordion][aria-expanded="true"]').forEach((open) => {
        if (open !== toggle) setExpanded(open, false);
      });
    }
    setExpanded(toggle, expand);

    if (expand) {
      const scroller = toggle.closest('#mobile-menu');
      const offset = toggle.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
      if (offset < 0) scroller.scrollTop += offset;
    }
  });

  /* ---------- Cart badge bump on count change ---------- */

  const observeCartBadge = () => {
    const toggle = document.getElementById('cart-drawer-toggle');
    if (!toggle || toggle.dataset.pBadgeObserved) return;
    toggle.dataset.pBadgeObserved = 'true';

    const readCount = () => toggle.querySelector('.thb-item-count')?.textContent.trim() || '';
    let previous = readCount();

    new MutationObserver(() => {
      const badge = toggle.querySelector('.thb-item-count');
      const current = readCount();
      if (!badge || current === previous) return;
      previous = current;
      if (!current) return;
      badge.classList.remove('is-bumped');
      void badge.offsetWidth;
      badge.classList.add('is-bumped');
    }).observe(toggle, { childList: true, characterData: true, subtree: true });
  };

  observeCartBadge();
  document.addEventListener('shopify:section:load', observeCartBadge);

  /* ---------- Shopify account sheet: hang it under the header ---------- */

  const placeAccountSheet = (account) => {
    const header = account.closest('.header');
    if (!header) return;
    const bar = header.getBoundingClientRect();
    const icons = account.closest('.thb-secondary-area')?.getBoundingClientRect() || bar;
    /* The open dialog sits in the top layer, whose right edge can differ from clientWidth
       (scrollbar gutter); read it back from the dialog itself once it is open. */
    const dialog = account.shadowRoot?.querySelector('dialog');
    let edge = document.documentElement.clientWidth;
    if (dialog?.open) edge = dialog.getBoundingClientRect().right + (parseFloat(getComputedStyle(dialog).right) || 0);
    account.style.setProperty('--p-account-top', `${Math.max(0, Math.round(bar.bottom)) + 8}px`);
    account.style.setProperty('--p-account-end', `${Math.max(12, Math.round(edge - icons.right))}px`);
  };

  ['pointerdown', 'keydown', 'open'].forEach((type) => {
    document.addEventListener(
      type,
      (event) => {
        const account = event.target.closest?.('shopify-account');
        if (!account) return;
        placeAccountSheet(account);
        /* Opening locks page scroll, which can change the viewport width. */
        requestAnimationFrame(() => requestAnimationFrame(() => placeAccountSheet(account)));
      },
      true
    );
  });
})();
